'use client';

import * as NavigationMenu from '@mcdougald/ui/src';
import { motion } from 'motion/react';
import Link from 'next/link';
import { type ReactNode, type SVGAttributes, useEffect, useState } from 'react';

import { RoundedTrevIcon } from '@/components/common/icons/personal/tm-icon';
import { PRIMARY_COLOR } from '@/constants/colors';
import { useScrollPosition } from '@/hooks/use-scroll-position';
import { cn } from '@/libraries/tailwind/cn';

import CommandMenu from '../common/command-menu';
import { FilledCoderLogo } from '../common/icons/coder-logo';

import LocaleSwitcher from './locale-switcher';
import { MobileNav } from './mobile-nav';
import Navbar from './navbar';
import ThemeSwitcher from './theme-switcher';

export type NavLink = {
  href: string;
  title: string;
  subtitle?: string;
  icon: (props: SVGAttributes<SVGSVGElement>) => ReactNode;
};

function HomePageLink() {
  return (
    <Link
      className="flex items-center justify-center gap-3"
      href="/apps/web/public"
    >
      <FilledCoderLogo
        svg={{
          width: '100px',
          height: '100px',
          className:
            'flex w-12 h-12 ease-in-back transition-transform rotate-0 hover:rotate-90 duration-1000',
        }}
        path={{ stroke: PRIMARY_COLOR }}
        color={PRIMARY_COLOR}
      />
      <div className="md:hidden font-serif text-xl font-semibold tracking-wider">
        Trevor McDougald
      </div>
    </Link>
  );
}

/**
 * Rendered in Default app/layout.tsx
 *
 *
 */
export const Header2 = ({ children }: { children?: ReactNode }) => (
  <header className="app-header absolute top-4 inset-x-0 z-50">
    <NavigationMenu.Root className="">
      <div className="bg-gray-900 mx-auto px-5 lg:px-4 w-[95vw] md:w-fit bg-text/70 rounded-xl">
        <div className="flex justify-between gap-7">
          <HomePageLink />
          <div className="flex h-14 pt-2 pr-2 pb-2 pl-2 items-center">
            <Navbar />
            <ThemeSwitcher />
            <LocaleSwitcher />
            <CommandMenu />
          </div>

          <div className="flex gap-3 items-center">
            <MobileNav />
            <div className="hidden md:block">{children}</div>
          </div>
        </div>
      </div>
      <NavigationMenu.Viewport className="bg-surface/90 backdrop-blur-md border border-border" />
    </NavigationMenu.Root>
  </header>
);

export const Header = ({ children }: { children?: ReactNode }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  // const t = useTranslations()
  const { scrollY } = useScrollPosition();
  console.log('[Header]', {
    isScrolled,
    scrollY,
  });
  useEffect(() => {
    const changeBackground = () => {
      if (window.scrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    globalThis.document.addEventListener('scroll', changeBackground);

    return () =>
      globalThis.document.removeEventListener('scroll', changeBackground);
  }, []);

  return (
    <motion.header
      className={cn('fixed inset-x-0 top-4 z-40 mx-auto flex h-[60px] px-5')}
      initial={{
        y: -100,
      }}
      animate={{
        y: 0,
      }}
      transition={{
        duration: 0.3,
      }}
    >
      <a
        href="#skip-nav"
        className="bg-background focus-visible:ring-ring rounded-xs shadow-xs focus-visible:ring-3 fixed left-4 top-4 -translate-y-20 border p-2 font-medium transition-transform focus-visible:translate-y-0 focus-visible:ring-offset-2"
      >
        <span>{'Skip'}</span>
      </a>
      <Link
        href="/"
        className="flex items-center justify-center gap-1 pl-3"
        // aria-label={t('layout.home')}
      >
        {/*<FilledCoderLogo*/}
        {/*  svg={{*/}
        {/*    width: '100px',*/}
        {/*    height: '100px',*/}
        {/*    className:*/}
        {/*      'flex w-12 h-12 ease-in-back transition-transform rotate-0 hover:rotate-90 duration-1000',*/}
        {/*  }}*/}
        {/*  path={{ stroke: PRIMARY_COLOR }}*/}
        {/*  color={PRIMARY_COLOR}*/}
        {/*/>*/}

        {/*<NewTMIcon svg={{ width: 40 }} />*/}
        <RoundedTrevIcon path={{ fill: 'white' }} svg={{ width: '48px' }} />
      </Link>
      <div
        className={cn(
          'header-container flex items-center content-center gap-2 bg-gray-50/99 hover:bg-gray-50/99  shadow-xs saturate-100 backdrop-blur-[10px] max-w-5xl justify-between rounded-2xl px-4 transition-colors mx-auto',
          isScrolled && 'bg-gray-50/90',
        )}
      >
        <Navbar />
        <ThemeSwitcher />
        <LocaleSwitcher />
        <CommandMenu />
        <MobileNav />
      </div>
      <>{children}</>
    </motion.header>
  );
};
